// The three symbols of a hosting code, and the address they open
// (arcade.md §16.1, R896).
//
// A hosting code is the theme alphabet's own code, three symbols long
// instead of four: `normalizeThemeCode` of `ui/settings.ts` states the
// rule for a theme and this states it for a hosting. The two are not one
// function, because the two lengths come from two figures of the data
// layer and a shared one would have to take the length as an argument at
// every call site.
//
// Nothing here talks to a server. A code the field refuses never reaches
// the socket, so a person who mistyped is told by the page and not by a
// refusal that travelled to the coordinator and back.

import { CUSTOM_CODE_HEADS, HOST_CODE_HEADS, HOST_CODE_LEN, THEME_CODE_ALPHABET } from '../gen/balance';

/**
 * A hosting code in the form the wire takes, or '' where the text is not
 * one. A person types a code in any case and with spaces around it.
 */
export function normalizeHostCode(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const code = raw.trim().toUpperCase();
  if (code.length !== HOST_CODE_LEN) return '';
  for (const ch of code) if (!THEME_CODE_ALPHABET.includes(ch)) return '';
  return code;
}

/** What a three-symbol code names: an arcade hosting of the coordinator,
 *  or a custom lobby of the meta server. */
export type CodeKind = 'arcade' | 'custom';

/**
 * What one code names, by its first symbol, or null where the text is no
 * code (R3087, arcade.md §16.1). A hosting code starts with a letter of
 * `HOST_CODE_HEADS`, and a custom lobby code with a digit of
 * `CUSTOM_CODE_HEADS`. The two services share the alphabet and the length,
 * so a page routes a typed code by its first symbol and asks no server
 * which one holds it.
 */
export function codeKind(raw: unknown): CodeKind | null {
  const head = normalizeHostCode(raw).charAt(0);
  if (head === '') return null;
  if (HOST_CODE_HEADS.includes(head)) return 'arcade';
  if (CUSTOM_CODE_HEADS.includes(head)) return 'custom';
  return null;
}

/**
 * The address that opens a hosting. The arcade page reads `?host=` at
 * load and sends the join once the socket is up.
 *
 * The server writes a link of its own into `ArcadeHosting.link`, and the
 * hosting window prints THAT one, because the coordinator knows the site
 * address and a page opened on a walk-in address does not. This is the
 * link the LOBBY builds, where no frame has arrived yet.
 */
export function hostLink(code: string): string {
  return `${location.origin}/arcade?host=${encodeURIComponent(code)}`;
}
